import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { LOGO } from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";
import { NUTRIENTS } from "./Nuna";
import { useI18n } from "../lib/LanguageContext";
import { localizeContent } from "../lib/translations";

export default function PrintPage() {
  const { lang } = useI18n();
  const tr = (value) => localizeContent(value, lang);
  const locale = lang === "ko" ? "ko-KR" : lang === "en" ? "en-US" : "id-ID";
  const { id } = useParams();
  const [rec, setRec] = useState(null); const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/nuna/analyses/${id}`).then(r => setRec(r.data)).catch(() => setErr("Analisis tidak ditemukan atau tidak dapat diakses.")); }, [id]);

  if (err) return <div className="max-w-3xl mx-auto py-16 text-center">{err}</div>;
  if (!rec) return <div className="max-w-3xl mx-auto py-16 text-center">Memuat...</div>;

  const s = rec.subject; const bmi = rec.bmi;
  const bmiCat = bmi ? tr(bmi<18.5?"Kurus":bmi<25?"Normal":bmi<30?"Overweight":"Obesitas") : "-";

  return (
    <div className="print-root max-w-4xl mx-auto p-6 print:p-0 bg-white" data-testid="print-page">
      <div className="print:hidden flex items-center justify-between mb-6">
        <Link to="/nuna" className="text-teal hover:underline flex items-center text-sm"><ArrowLeft className="w-4 h-4 mr-1"/>{tr("Kembali")}</Link>
        <Button onClick={()=>window.print()} className="rounded-full bg-teal hover:bg-teal/90 text-white" data-testid="print-btn"><Printer className="w-4 h-4 mr-1"/> {tr("Cetak / Simpan PDF")}</Button>
      </div>

      <div className="border-b-2 border-teal pb-4 mb-6 flex items-center justify-between">
        <img src={LOGO} alt="GIDZA" className="h-14"/>
        <div className="text-right text-xs">
          <div className="font-display text-2xl text-teal font-medium">NUNA Report</div>
          <div className="text-muted-foreground">{new Date(rec.created_at).toLocaleString(locale)}</div>
        </div>
      </div>

      <section className="mb-6">
        <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-teal mb-2">{tr("Data Subjek")}</h2>
        <div className="grid grid-cols-4 gap-3 text-sm">
          <Info l={tr("Nama")} v={s.name || "-"}/>
          <Info l={tr("Usia")} v={`${s.age || "-"} ${tr("th")}`}/>
          <Info l={tr("Jenis")} v={tr(s.sex === "male" ? "Pria" : "Wanita")}/>
          <Info l={tr("Aktivitas")} v={tr(s.activity || "-")}/>
          <Info l={tr("Berat Badan")} v={`${s.weight || "-"} kg`}/>
          <Info l={tr("Tinggi Badan")} v={`${s.height || "-"} cm`}/>
          <Info l="BMI" v={`${bmi || "-"} · ${bmiCat}`}/>
          <Info l={tr("Standar")} v={tr(rec.standard)}/>
        </div>
      </section>

      <section className="mb-6">
        <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-teal mb-2">{tr("Item Makanan")}</h2>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b-2 border-teal">
              <th className="text-left py-2">{tr("Makanan")}</th>
              <th className="text-right py-2">{tr("Berat (g)")}</th>
              <th className="text-right py-2">kcal</th>
              <th className="text-right py-2">Protein (g)</th>
              <th className="text-right py-2">{tr("Lemak (g)")}</th>
              <th className="text-right py-2">{tr("Karbo (g)")}</th>
            </tr>
          </thead>
          <tbody>
            {rec.entries.map((e, i) => (
              <tr key={i} className="border-b border-teal/10">
                <td className="py-1.5">{e.name}</td>
                <td className="text-right">{e.grams}</td>
                <td className="text-right">{Number(e.kcal).toFixed(1)}</td>
                <td className="text-right">{Number(e.protein).toFixed(1)}</td>
                <td className="text-right">{Number(e.fat).toFixed(1)}</td>
                <td className="text-right">{Number(e.carb).toFixed(1)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mb-6">
        <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-teal mb-2">{tr("Ringkasan Gizi vs AKG")}</h2>
        {["macro","vitamin","mineral"].map(g => {
          const items = NUTRIENTS.filter(n => n.g === g);
          const label = tr(g === "macro" ? "Makronutrien" : g === "vitamin" ? "Vitamin" : "Mineral");
          return (
            <div key={g} className="mb-4">
              <div className="text-xs uppercase tracking-widest text-teal font-semibold mb-1">{label}</div>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-teal">
                    <th className="text-left py-1.5">{tr("Nutrient")}</th>
                    <th className="text-right py-1.5">{tr("Total")}</th>
                    <th className="text-right py-1.5">{tr("AKG")}</th>
                    <th className="text-right py-1.5">%</th>
                    <th className="text-left py-1.5 pl-3 w-1/4">{tr("Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(n => {
                    const p = rec.percentages[n.k] || 0;
                    const status = tr(p >= 80 && p <= 120 ? "Cukup" : p < 80 ? "Kurang" : "Berlebih");
                    return (
                      <tr key={n.k} className="border-b border-teal/10">
                        <td className="py-1">{tr(n.l)}</td>
                        <td className="text-right">{(rec.totals[n.k]||0).toFixed(2)} {n.u}</td>
                        <td className="text-right">{rec.standard_values[n.k]} {n.u}</td>
                        <td className="text-right font-semibold">{p}%</td>
                        <td className="pl-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-teal-soft rounded-full overflow-hidden"><div className="h-full bg-teal" style={{width:`${Math.min(150,p)}%`}}/></div>
                            <span className={p>=80 && p<=120 ? "text-teal text-[10px]" : "text-orange-500 text-[10px]"}>{status}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </section>

      <div className="mt-8 pt-4 border-t border-teal/20 text-xs text-muted-foreground italic">
        Dihasilkan oleh NUNA — GIDZA INSTITUTE. Dokumen ini bersifat referensi analisis gizi, bukan pengganti konsultasi ahli gizi terlisensi.
      </div>
    </div>
  );
}

function Info({ l, v }) {
  return <div><div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</div><div className="font-medium">{v}</div></div>;
}
